import type { FC } from "react";
import { Fragment, useMemo } from "react";
import { Button, Card, Table } from "@ptsd/uikit";
import { Download } from "@tamagui/lucide-icons";
import { useTranslation } from "react-i18next";
import { Anchor, ScrollView, Text, XStack } from "tamagui";

import type { RouterOutputs } from "~/utils/api";
import { api } from "~/utils/api";
import { dateToReadableDMY } from "~/utils/date-to-readable";
import config from "~/config";
import { scalesColorsByLevel } from "../scales";
import { PersonResearchDialog } from "./person-research-dialog";

const WIDTH_FIRST_CELL = 400;
const MIN_WIDTH_CELL = 180;
const PDF_ROW = { name: "", code: "download" };

type ScaleItem =
  RouterOutputs["personal"]["types"][number]["scaleGroups"][number]["scaleItems"][number];
type SurveyScore =
  RouterOutputs["personal"]["surveys"][number]["scores"][number];
export interface PersonResearchTableProps {
  personId: string;
  type: string;
  group: "scale" | "sphere";
  scales: ScaleItem[];
  pdfDownload?: boolean;
}

export const PersonResearchTable: FC<PersonResearchTableProps> = ({
  personId,
  type,
  group,
  scales,
  pdfDownload = true,
}) => {
  const { t } = useTranslation("person");

  const rows = useMemo(
    () => (pdfDownload ? [...scales, PDF_ROW] : scales),
    [scales, pdfDownload],
  );

  const [data] = api.personal.surveys.useSuspenseQuery({
    type,
    personId,
  });

  if (data.length === 0) return null;

  return (
    <Fragment>
      <ScrollView
        horizontal
        width={"100%"}
        showsHorizontalScrollIndicator={true}
        contentContainerStyle={{ minWidth: "100%" }}
      >
        <Table
          cellHeight="$6"
          borderRadius={0}
          borderWidth={0}
          alignCells={{ x: "center", y: "center" }}
          alignHeaderCells={{ y: "center", x: "center" }}
        >
          <Table.Head>
            <Table.Row backgroundColor={"$gray-50"} borderTopWidth={"$0.1"}>
              <Table.HeaderCell
                backgroundColor={"$gray-50"}
                cellLocation="first"
                minWidth={WIDTH_FIRST_CELL}
                maxWidth={WIDTH_FIRST_CELL}
              >
                <Text fontFamily={"$subtitle"} fontSize={"$3"}>
                  {t(`table.${group}`)}
                </Text>
              </Table.HeaderCell>
              {data?.map(({ startedAt, id }) => (
                <DateCell key={id} date={startedAt} />
              ))}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {rows.map((row, index) => (
              <Table.Row
                key={index}
                borderBottomWidth={index === rows.length - 1 ? 0 : "$0.1"}
              >
                <Table.Cell
                  minWidth={WIDTH_FIRST_CELL}
                  maxWidth={WIDTH_FIRST_CELL}
                  cellLocation="first"
                  justifyContent="flex-start"
                >
                  <Text
                    fontFamily={"$body"}
                    fontSize={"$2"}
                    textAlign="left"
                    numberOfLines={1}
                  >
                    {row.name}
                  </Text>
                </Table.Cell>
                {data?.map(({ id, scores, status }) => {
                  if (row.code === PDF_ROW.code) {
                    if (status != "finished") return <EmptyCell key={id} />;
                    else return <DownloadCell key={id} id={id} />;
                  }

                  const item = scores[row.code];
                  if (!item) return <EmptyCell key={id} />;
                  return <ScoreCell key={id} item={item} scale={row} />;
                })}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </ScrollView>
    </Fragment>
  );
};

const DateCell: FC<{ date: Date }> = ({ date }) => {
  return (
    <Table.HeaderCell minWidth={MIN_WIDTH_CELL} cellLocation="middle">
      <Text fontFamily={"$subtitle"} fontSize={"$3"}>
        {dateToReadableDMY(date)}
      </Text>
    </Table.HeaderCell>
  );
};

const ScoreCell: FC<{ item: SurveyScore; scale: ScaleItem }> = ({
  item,
  scale,
}) => {
  const { color, bgColor } = scalesColorsByLevel(item.level);

  return (
    <Table.Cell
      alignSelf="center"
      cellLocation="middle"
      minWidth={MIN_WIDTH_CELL}
    >
      <XStack
        gap={"$2"}
        justifyContent="space-evenly"
        alignItems="center"
        width={130}
      >
        <Card
          flex={1}
          alignItems="center"
          variant="borderless"
          paddingHorizontal={"$3"}
          paddingVertical={"$0.5"}
          backgroundColor={bgColor}
        >
          <Text
            fontFamily={"$body"}
            fontSize={"$2"}
            fontWeight={"$6"}
            color={color}
          >
            {item.percentage}/100
          </Text>
        </Card>
        <PersonResearchDialog scale={{ ...scale, ...item }} />
      </XStack>
    </Table.Cell>
  );
};

const EmptyCell: FC<{ text?: string }> = ({ text }) => {
  const { t } = useTranslation("person");
  return (
    <Table.Cell
      alignSelf="center"
      cellLocation="middle"
      minWidth={MIN_WIDTH_CELL}
    >
      <Text fontFamily={"$body"} fontSize={"$2"}>
        {text ?? t("table.empty")}
      </Text>
    </Table.Cell>
  );
};

const DownloadCell: FC<{ id: string }> = ({ id }) => {
  const { t } = useTranslation("person");
  const href = useMemo(
    () => new URL(`/export/pdf/survey/${id}`, config.API_URL).href,
    [id],
  );

  return (
    <Table.Cell minWidth={MIN_WIDTH_CELL} cellLocation="middle">
      <Anchor href={href} textDecorationLine="none" asChild>
        <Button
          size="sm"
          rounded="md"
          height={"$4"}
          paddingVertical={"$1"}
          paddingHorizontal={"$1"}
          variant="outlined"
          icon={<Download size={16} />}
        >
          {t("table.download")}
        </Button>
      </Anchor>
    </Table.Cell>
  );
};
